import { z } from 'zod';
import { KI_BILD_ANBIETER, KI_TEXT_ANBIETER, type KiArt } from './ki';

/**
 * KI je Aufgabe: Mehrere Zugänge (je Anbieter ein Schlüssel) und je Aufgabe Anbieter, Modell und Ersatzmodell.
 * Gilt für die KI von DXL (Plattform) und für den eigenen Schlüssel eines Marktplatzes gleich.
 * Modellnamen stehen hier bewusst nicht – sie kommen aus der Modellliste des Anbieters oder werden eingetragen.
 */

export interface KiAufgabeInfo {
  label: string;
  text: string;
  /** text = Sprachmodell (auch mit Bildern als Eingabe), bild = Bild-KI (erzeugt Bilder) */
  art: KiArt;
  /** braucht ein Modell, das Bilder lesen kann */
  vision?: boolean;
}

export const KI_AUFGABEN = {
  standard: { label: 'Standard', art: 'text', text: 'Gilt für alle Text-Aufgaben ohne eigene Zuordnung.' },
  anreicherung: { label: 'Anreicherung', art: 'text', text: 'Produkttexte erzeugen und verbessern, fehlende Felder ergänzen, freie Anweisungen.' },
  uebersetzung: { label: 'Übersetzung', art: 'text', text: 'Produkttexte in andere Sprachen, mit Glossar des Marktplatzes.' },
  kategorisierung: { label: 'Kategorisierung', art: 'text', text: 'Produkte den Kategorien des Marktplatzes zuordnen.' },
  extraktion: { label: 'Attribut-Extraktion', art: 'text', vision: true, text: 'Merkmale aus Titel, Beschreibung und Bildern in die Attribute der Kategorie.' },
  normalisierung: { label: 'Normalisierung', art: 'text', text: 'Freie Händlerwerte auf Auswahllisten abbilden.' },
  pruefung: { label: 'Prüfung', art: 'text', text: 'Widersprüche, unzulässige Inhalte, Werbeaussagen und Rechtschreibung prüfen.' },
  bildanalyse: { label: 'Bildverständnis', art: 'text', vision: true, text: 'Hauptbild-Prüfung (Grenzfälle), Alt-Texte, Bild passt zum Produkt.' },
  bilderzeugung: { label: 'Bilderzeugung', art: 'bild', text: 'Freistellen, Hintergründe und neue Produkt- oder Szenenbilder.' },
  werbung_einbau: { label: 'Werbe-Einbau', art: 'text', text: 'Assistent für den Einbau der Werbeflächen im Shop.' },
} as const satisfies Record<string, KiAufgabeInfo>;
export type KiAufgabe = keyof typeof KI_AUFGABEN;
export const KI_AUFGABE_LISTE = Object.keys(KI_AUFGABEN) as KiAufgabe[];

/** Bisherige Anlässe in ki_verbrauch → Aufgabe (Buchungen vor der Aufgaben-Spalte) */
export function kiAufgabeAusAnlass(anlass: string): KiAufgabe {
  if (anlass.startsWith('anreicherung')) return 'anreicherung';
  if (anlass.startsWith('kategorie')) return 'kategorisierung';
  if (anlass.startsWith('werbung.einbau')) return 'werbung_einbau';
  const a = anlass.split('.')[0] as KiAufgabe;
  return a in KI_AUFGABEN ? a : 'standard';
}

/** Anbieter mit den Arten, die ein Schlüssel abdeckt (ein Schlüssel je Anbieter) */
export interface KiAnbieterEintrag {
  label: string;
  arten: readonly KiArt[];
  schluesselLabel: string;
  /** Modellliste per API abrufbar */
  modellliste: boolean;
  /** Modell muss nicht angegeben werden (Stability: Dienst über die Adresse) */
  modellOptional?: boolean;
}

export const KI_ANBIETER = {
  anthropic: { label: 'Anthropic (Claude)', arten: ['text'], schluesselLabel: KI_TEXT_ANBIETER.anthropic.schluesselLabel, modellliste: true },
  openai: { label: 'OpenAI', arten: ['text', 'bild'], schluesselLabel: KI_TEXT_ANBIETER.openai.schluesselLabel, modellliste: true },
  gemini: { label: 'Google Gemini', arten: ['text', 'bild'], schluesselLabel: KI_TEXT_ANBIETER.gemini.schluesselLabel, modellliste: true },
  stability: { label: 'Stability AI', arten: ['bild'], schluesselLabel: KI_BILD_ANBIETER.stability.schluesselLabel, modellliste: false, modellOptional: true },
  replicate: { label: 'Replicate', arten: ['bild'], schluesselLabel: KI_BILD_ANBIETER.replicate.schluesselLabel, modellliste: false },
} as const satisfies Record<string, KiAnbieterEintrag>;
export type KiAnbieter = keyof typeof KI_ANBIETER;
export const KI_ANBIETER_LISTE = Object.keys(KI_ANBIETER) as KiAnbieter[];

export const kiAnbieterKann = (anbieter: string, art: KiArt): boolean =>
  ((KI_ANBIETER as Record<string, KiAnbieterEintrag>)[anbieter]?.arten ?? []).includes(art);

/** Modell aus der Liste des Anbieters */
export interface KiModellInfo {
  id: string;
  art: KiArt;
  name?: string;
  /** liest Bilder (laut Anbieter oder bekannt) */
  vision?: boolean;
}

/** Stand eines Zugangs – der Schlüssel verlässt den Server nie */
export interface KiZugangStand {
  anbieter: KiAnbieter;
  schluesselGesetzt: boolean;
  geprueftAm: string | null;
  /** letzte Meldung des Anbieters bei fehlgeschlagener Prüfung */
  fehler: string | null;
  modelle: KiModellInfo[];
  modelleAm: string | null;
  geaendertAm: string | null;
}

export const KiZugangSpeichernSchema = z.object({
  /** leer oder „••…“ = bisherigen Schlüssel behalten */
  schluessel: z.string().max(500).optional(),
  loeschen: z.boolean().optional(),
});
export type KiZugangSpeichernEingabe = z.input<typeof KiZugangSpeichernSchema>;

const ModellId = z.string().trim().max(160).regex(/^[A-Za-z0-9._:\/@-]*$/, 'Bitte nur die Modell-ID angeben').default('');
const AnbieterFeld = z.union([z.enum(KI_ANBIETER_LISTE as [KiAnbieter, ...KiAnbieter[]]), z.literal('')]).default('');

export const KiAufgabeSchema = z
  .object({
    aufgabe: z.enum(KI_AUFGABE_LISTE as [KiAufgabe, ...KiAufgabe[]]),
    anbieter: AnbieterFeld,
    modell: ModellId,
    ersatzAnbieter: AnbieterFeld,
    ersatzModell: ModellId,
  })
  .superRefine((d, ctx) => {
    const art = KI_AUFGABEN[d.aufgabe].art;
    for (const [a, m, pa, pm] of [[d.anbieter, d.modell, 'anbieter', 'modell'], [d.ersatzAnbieter, d.ersatzModell, 'ersatzAnbieter', 'ersatzModell']] as const) {
      if (!a) {
        if (m) ctx.addIssue({ code: 'custom', path: [pa], message: 'Bitte den Anbieter wählen' });
        continue;
      }
      if (!kiAnbieterKann(a, art)) ctx.addIssue({ code: 'custom', path: [pa], message: art === 'bild' ? 'Dieser Anbieter erzeugt keine Bilder' : 'Dieser Anbieter bietet keine Text-KI' });
      if (!m && !(KI_ANBIETER as Record<string, KiAnbieterEintrag>)[a]?.modellOptional) ctx.addIssue({ code: 'custom', path: [pm], message: 'Bitte das Modell angeben' });
    }
    if (!d.anbieter && d.ersatzAnbieter) ctx.addIssue({ code: 'custom', path: ['anbieter'], message: 'Ersatz nur zusammen mit einem Hauptmodell' });
    if (d.anbieter && d.anbieter === d.ersatzAnbieter && d.modell === d.ersatzModell) ctx.addIssue({ code: 'custom', path: ['ersatzModell'], message: 'Ersatzmodell muss sich vom Hauptmodell unterscheiden' });
  });
export const KiAufgabenSchema = z.object({ aufgaben: z.array(KiAufgabeSchema).max(KI_AUFGABE_LISTE.length) });
export type KiAufgabeEingabe = z.input<typeof KiAufgabeSchema>;

export interface KiAufgabeZuordnung {
  aufgabe: KiAufgabe;
  anbieter: KiAnbieter | '';
  modell: string;
  ersatzAnbieter: KiAnbieter | '';
  ersatzModell: string;
  geaendertAm: string | null;
}

/** Wirksam für eine Aufgabe: eigene Zuordnung oder Standard (nur Text-Aufgaben) */
export function kiZuordnungWirksam<T extends { aufgabe: string; anbieter: string }>(aufgabe: KiAufgabe, zuordnungen: readonly T[]): { zuordnung: T; ausStandard: boolean } | null {
  const eigene = zuordnungen.find((z) => z.aufgabe === aufgabe && z.anbieter);
  if (eigene) return { zuordnung: eigene, ausStandard: false };
  if (KI_AUFGABEN[aufgabe].art !== 'text' || aufgabe === 'standard') return null;
  const standard = zuordnungen.find((z) => z.aufgabe === 'standard' && z.anbieter);
  return standard ? { zuordnung: standard, ausStandard: true } : null;
}

/** Alles für Zugänge und Aufgaben einer Seite (Plattform oder Marktplatz) */
export interface KiAufgabenStand {
  zugaenge: KiZugangStand[];
  aufgaben: KiAufgabeZuordnung[];
}
